import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { messagesApi } from '../services/messagesApi';
import { ArrowLeft, Globe } from 'lucide-react';
import { useAuth } from '../state/auth';
import { useMessagesSocket } from '../hooks/useMessagesSocket';

/**
 * Converts markdown links [text](url) and raw URLs in a string
 * into clickable components.
 */
const linkify = (text, isBot, isLight) => {
    if (!text) return text;

    // Determine colors based on context (bot vs guest bubbles, light vs dark theme)
    // Bot bubble background:
    // - Light theme: bg-neutral-900 (dark) -> text-blue-300 link
    // - Dark theme: bg-white (light) -> text-blue-600 link
    // Guest bubble: 
    // - Always light-ish -> text-blue-600 link
    let linkColor = 'text-blue-600 underline';

    // Determine background brightness to pick best link color
    const isDarkBg = (isBot && isLight) || (!isBot && !isLight);
    linkColor = isDarkBg ? 'text-blue-300 underline' : 'text-blue-600 underline';

    const pattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s),]+)/g;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        if (match[1] && match[2]) {
            // Markdown link: [text](url)
            parts.push(
                <a
                    key={match.index}
                    href={match[2]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${linkColor} underline-offset-2 hover:opacity-80 transition-opacity`}
                >
                    {match[1]}
                </a>
            );
        } else {
            // Raw URL
            parts.push(
                <a
                    key={match.index}
                    href={match[3]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${linkColor} underline-offset-2 hover:opacity-80 transition-opacity break-all`}
                >
                    {match[3]}
                </a>
            );
        }
        lastIndex = pattern.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
};

const Messages = () => {
    const { api, user, tabActiveHotelId } = useAuth();
    const outletContext = useOutletContext() || {};
    const { theme = 'dark', properties = [] } = outletContext;
    const isLight = theme === 'light';

    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isLoadingConversations, setIsLoadingConversations] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [displayLanguage, setDisplayLanguage] = useState('english'); // 'english' or 'original'

    const { latestMessage } = useMessagesSocket();
    const processedMessageId = useRef(null);
    const messagesContainerRef = useRef(null);
    const conversationListRef = useRef(null);
    const sectionRef = useRef(null);
    const headerRef = useRef(null);
    const savedScrollPosition = useRef(0);
    const [containerHeight, setContainerHeight] = useState('calc(100vh - 200px)');

    // Calculate container height dynamically
    useEffect(() => {
        const calculateHeight = () => {
            const viewportHeight = window.innerHeight;
            const sectionTop = sectionRef.current?.getBoundingClientRect().top || 120;
            const bottomPadding = 24; // Small bottom padding
            const cardPadding = 48; // Card padding (p-6 = 24px * 2)
            const availableHeight = viewportHeight - sectionTop - bottomPadding - cardPadding;
            setContainerHeight(`${Math.max(300, availableHeight)}px`);
        };

        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        // Recalculate after a short delay to ensure layout is complete
        const timeout = setTimeout(calculateHeight, 100);

        return () => {
            window.removeEventListener('resize', calculateHeight);
            clearTimeout(timeout);
        };
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!latestMessage || processedMessageId.current === latestMessage.id) return;
        processedMessageId.current = latestMessage.id;

        if (selectedConversation && selectedConversation.user.id === latestMessage.user) {
            setMessages(prev => [...prev, {
                id: latestMessage.id,
                user: latestMessage.user,
                message: latestMessage.message,
                created_on: latestMessage.created_on,
                role: latestMessage.role,
                output_english: latestMessage.output_english,
                output_original: latestMessage.output_original,
                user_input_english: latestMessage.user_input_english,
                user_input_original: latestMessage.user_input_original
            }]);
        }

        setConversations(prev => {
            const index = prev.findIndex(c => c.user.id === latestMessage.user);
            if (index !== -1) {
                const updatedConv = {
                    ...prev[index],
                    lastMessage: {
                        message: latestMessage.message,
                        created_on: latestMessage.created_on,
                        role: latestMessage.role,
                        output_english: latestMessage.output_english,
                        output_original: latestMessage.output_original,
                        user_input_english: latestMessage.user_input_english,
                        user_input_original: latestMessage.user_input_original
                    },
                    unreadCount: (selectedConversation?.user?.id === latestMessage.user) ? 0 : (prev[index].unreadCount + 1)
                };
                const newList = [...prev];
                newList.splice(index, 1);
                return [updatedConv, ...newList];
            } else {
                const property = isAllMode ? properties.find(p => p.id == latestMessage.hotel_id) : null;
                const newConv = {
                    user: {
                        id: latestMessage.user,
                        name: latestMessage.user,
                        avatar: null
                    },
                    property: property ? { id: property.id, name: property.name } : (isAllMode ? { id: latestMessage.hotel_id, name: `Property ${latestMessage.hotel_id}` } : undefined),
                    lastMessage: {
                        message: latestMessage.message,
                        created_on: latestMessage.created_on,
                        role: latestMessage.role,
                        output_english: latestMessage.output_english,
                        output_original: latestMessage.output_original,
                        user_input_english: latestMessage.user_input_english,
                        user_input_original: latestMessage.user_input_original
                    },
                    unreadCount: 1,
                    messages: undefined
                };
                return [newConv, ...prev];
            }
        });
    }, [latestMessage, selectedConversation]);

    const fetchConversations = async (pageNum) => {
        setIsLoadingConversations(true);
        const isAllMode = tabActiveHotelId === 'all';
        if (!isAllMode && !tabActiveHotelId) return;
        try {
            const response = isAllMode
                ? await messagesApi.getAllConversations(api, pageNum)
                : await messagesApi.getConversations(api, tabActiveHotelId, pageNum);
            if (pageNum === 1) {
                setConversations(response.data);
            } else {
                setConversations(prev => [...prev, ...response.data]);
            }
            setHasMore(response.hasMore);
        } catch (error) {
            console.error("Failed to fetch conversations", error);
        } finally {
            setIsLoadingConversations(false);
        }
    };

    useEffect(() => {
        if (tabActiveHotelId) {
            setSelectedConversation(null);
            setMessages([]);
            setPage(1);
            fetchConversations(1);
        }
    }, [tabActiveHotelId, api]);

    const isAllMode = tabActiveHotelId === 'all';

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchConversations(nextPage);
    };

    // Infinite scroll handler for conversations
    const handleConversationScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const nearBottom = scrollHeight - scrollTop - clientHeight < 100;

        if (nearBottom && hasMore && !isLoadingConversations) {
            handleLoadMore();
        }
    };

    const handleSelectConversation = async (conversation) => {
        // Save scroll position before switching to messages
        if (conversationListRef.current) {
            savedScrollPosition.current = conversationListRef.current.scrollTop;
        }
        setSelectedConversation(conversation);
        // In all-mode the hotelId comes from the conversation's property, otherwise use tab's hotel
        const hotelId = isAllMode ? conversation.property?.id : tabActiveHotelId;
        if (!hotelId) return;
        try {
            const msgs = await messagesApi.getMessages(api, conversation.user.id, hotelId);
            setMessages(msgs);
            setConversations(prev => prev.map(c =>
                c.user.id === conversation.user.id ? { ...c, unreadCount: 0 } : c
            ));
        } catch (error) {
            console.error("Failed to fetch messages", error);
            setMessages([]);
        } finally {
            setIsLoadingMessages(false);
        }
    };

    const handleBackToConversations = () => {
        setSelectedConversation(null);
        setMessages([]);
        // Restore scroll position after component re-renders
        setTimeout(() => {
            if (conversationListRef.current) {
                conversationListRef.current.scrollTop = savedScrollPosition.current;
            }
        }, 0);
    };

    // Scroll to bottom when messages change
    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatRelativeTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        if (messageDate.getTime() === today.getTime()) {
            return 'Today';
        }
        if (messageDate.getTime() === yesterday.getTime()) {
            return 'Yesterday';
        }
        // Show date like "Dec 12"
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    const formatFullDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const isToday = date.toDateString() === today.toDateString();
        const isYesterday = date.toDateString() === yesterday.toDateString();

        if (isToday) return 'Today';
        if (isYesterday) return 'Yesterday';

        return date.toLocaleDateString([], {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getDateKey = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toDateString();
    };

    const getInitial = (name) => {
        if (!name) return '?';
        return name.charAt(0).toUpperCase();
    };

    return (
        <section ref={sectionRef} className="overflow-hidden">
            <div
                className={`rounded-2xl sm:rounded-3xl backdrop-blur-2xl border p-3 sm:p-6 overflow-hidden h-full flex flex-col ${isLight
                    ? "bg-white border-neutral-200 shadow-[0_18px_40px_rgba(15,23,42,0.1)]"
                    : "bg-[#1b1e21] border-white/5 shadow-[0_18px_60px_rgba(0,0,0,0.85)]"
                    }`}
            >
                {/* <div ref={headerRef} className="mb-3 sm:mb-4 flex-shrink-0">
                    <h2 className="text-sm sm:text-base font-semibold tracking-tight">Guest Conversations</h2>
                    <p className={`mt-0.5 sm:mt-1 text-[10px] sm:text-xs ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                        View messages your concierge has handled with in-house guests.
                    </p>
                </div> */}

                <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 overflow-hidden" style={{ height: containerHeight }}>
                    {(!isMobile || !selectedConversation) && (
                        <div
                            className={`w-full lg:w-72 rounded-xl sm:rounded-2xl overflow-hidden flex flex-col ${isLight
                                ? "bg-neutral-50 border border-neutral-200"
                                : "bg-[#1b1e21]"
                                }`}
                        >
                            <div
                                className={`px-3 sm:px-4 py-2 sm:py-2.5 flex items-center justify-between flex-shrink-0 ${isLight ? "text-neutral-500" : "text-neutral-500"
                                    }`}
                            >
                                <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em]">Guest Messages</span>
                                <button
                                    onClick={() => setDisplayLanguage(prev => prev === 'english' ? 'original' : 'english')}
                                    className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] sm:text-xs font-medium transition ${isLight
                                        ? "bg-neutral-100 hover:bg-neutral-200 text-neutral-700"
                                        : "bg-white/10 hover:bg-white/20 text-white"
                                        }`}
                                >
                                    <Globe size={12} />
                                    <span>{displayLanguage === 'english' ? 'EN' : 'ORG'}</span>
                                </button>
                            </div>
                            <div className={`flex-shrink-0 w-full ${isLight ? "h-[2px] bg-neutral-300" : "h-[2px] bg-white/50"}`} />

                            <div ref={conversationListRef} className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} onScroll={handleConversationScroll}>
                                {isLoadingConversations && conversations.length === 0 ? (
                                    <div className={`p-4 text-center text-xs ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                        Loading conversations...
                                    </div>
                                ) : conversations.length === 0 ? (
                                    <div className={`p-4 text-center text-xs ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                        No conversations yet
                                    </div>
                                ) : (
                                    <>
                                        {conversations.map((conversation) => {
                                            const active = selectedConversation?.user?.id === conversation.user.id;
                                            return (
                                                <button
                                                    key={conversation.user.id}
                                                    className={`w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 flex items-start gap-2 sm:gap-3 border-b text-xs transition ${isLight
                                                        ? active
                                                            ? "bg-white border-neutral-200"
                                                            : "border-neutral-200 hover:bg-neutral-100/70"
                                                        : active
                                                            ? "bg-white/10 border-white/10"
                                                            : "border-white/5 hover:bg-white/[0.04]"
                                                        }`}
                                                    onClick={() => handleSelectConversation(conversation)}
                                                >
                                                    <div
                                                        className={`h-7 w-7 sm:h-8 sm:w-8 rounded-xl sm:rounded-2xl flex items-center justify-center text-[10px] sm:text-xs font-medium border flex-shrink-0 ${isLight
                                                            ? "bg-neutral-900 text-white border-neutral-900"
                                                            : "bg-white/10 text-white border-white/20"
                                                            }`}
                                                    >
                                                        {getInitial(conversation.user.name)}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0, width: 0 }}>
                                                        <div className="flex items-center justify-between gap-1 mb-0.5">
                                                            <span className="font-medium truncate text-[11px] sm:text-xs block">
                                                                {conversation.user.name || 'Unknown Guest'}
                                                            </span>
                                                            <span className={`text-[9px] sm:text-[10px] flex-shrink-0 ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                                                {formatRelativeTime(conversation.lastMessage?.created_on)}
                                                            </span>
                                                        </div>
                                                        {/* Property badge – visible in All Properties mode */}
                                                        {isAllMode && conversation.property?.name && (
                                                            <span className={`inline-block mb-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-medium leading-none ${
                                                                isLight
                                                                    ? 'bg-neutral-200 text-neutral-600'
                                                                    : 'bg-white/10 text-neutral-400'
                                                            }`}>
                                                                {conversation.property.name}
                                                            </span>
                                                        )}
                                                        <p className={`text-[10px] sm:text-[11px] truncate block ${isLight ? "text-neutral-600" : "text-neutral-400"}`}>
                                                            {conversation.lastMessage
                                                                ? linkify(
                                                                    displayLanguage === 'original'
                                                                        ? (conversation.lastMessage.output_original ||
                                                                            conversation.lastMessage.user_input_original ||
                                                                            conversation.lastMessage.message ||
                                                                            conversation.lastMessage.output_english ||
                                                                            conversation.lastMessage.user_input_english)
                                                                        : (conversation.lastMessage.output_english ||
                                                                            conversation.lastMessage.user_input_english ||
                                                                            conversation.lastMessage.output_original ||
                                                                            conversation.lastMessage.user_input_original ||
                                                                            conversation.lastMessage.message),
                                                                    false, isLight)
                                                                : 'No messages'}
                                                        </p>
                                                        {conversation.unreadCount > 0 && (
                                                            <div className="mt-0.5 flex items-center">
                                                                <span className="ml-auto inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                        {isLoadingConversations && conversations.length > 0 && (
                                            <div className={`p-3 text-center text-xs ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                                Loading more...
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            {/* Horizontal Divider - Mobile only */}
                            <div className={`lg:hidden h-px w-full ${isLight ? "bg-neutral-300" : "bg-white/10"}`} />
                        </div>
                    )}

                    {/* Vertical Divider - Desktop only */}
                    <div className={`hidden lg:block w-px self-stretch ${isLight ? "bg-neutral-300" : "bg-white/10"}`} />

                    {(!isMobile || selectedConversation) && (
                        <div
                            className={`flex-1 rounded-xl sm:rounded-2xl overflow-hidden flex flex-col ${isLight
                                ? "bg-neutral-50 border border-neutral-200"
                                : "bg-[#1b1e21]"
                                }`}
                        >
                            {selectedConversation ? (
                                <>
                                    <div className="flex-1 flex flex-col h-full overflow-hidden">
                                        <div
                                            className={`px-3 sm:px-5 py-2.5 sm:py-3 border-b flex items-center justify-between flex-shrink-0 ${isLight ? "border-neutral-200" : "border-white/5"
                                                }`}
                                        >
                                            <div className="flex items-center gap-2 sm:gap-3">
                                                {isMobile && (
                                                    <button
                                                        onClick={handleBackToConversations}
                                                        className={`p-1.5 -ml-1 rounded-lg transition ${isLight ? "hover:bg-neutral-100 active:bg-neutral-200" : "hover:bg-white/10 active:bg-white/20"
                                                            }`}
                                                    >
                                                        <ArrowLeft size={18} />
                                                    </button>
                                                )}
                                                <div
                                                    className={`h-8 w-8 sm:h-9 sm:w-9 rounded-xl sm:rounded-2xl flex items-center justify-center text-[10px] sm:text-xs font-medium border ${isLight
                                                        ? "bg-neutral-900 text-white border-neutral-900"
                                                        : "bg-white/10 text-white border-white/20"
                                                        }`}
                                                >
                                                    {getInitial(selectedConversation.user.name)}
                                                </div>
                                                 <div>
                                                    <div className="text-xs sm:text-sm font-semibold tracking-tight">
                                                        {selectedConversation.user.name || 'Unknown Guest'}
                                                    </div>
                                                    {isAllMode && selectedConversation.property?.name && (
                                                        <div className={`text-[10px] leading-tight ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                                            {selectedConversation.property.name}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-5 py-3 sm:py-4 space-y-2.5 sm:space-y-3 text-xs sm:text-sm scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                            {isLoadingMessages ? (
                                                <div className={`text-center py-8 ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                                    Loading messages...
                                                </div>
                                            ) : messages.length === 0 ? (
                                                <div className={`text-center py-8 ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                                    No messages in this conversation
                                                </div>
                                            ) : (
                                                messages.map((msg, idx) => {
                                                    const currentDateKey = getDateKey(msg.created_on);
                                                    const prevDateKey = idx > 0 ? getDateKey(messages[idx - 1].created_on) : null;
                                                    const showDateSeparator = idx === 0 || currentDateKey !== prevDateKey;

                                                    // Determine text to show based on language
                                                    // NOTE: Each DB row contains BOTH user_input and output fields
                                                    let userInput = null;
                                                    let assistantOutput = null;

                                                    if (displayLanguage === 'original') {
                                                        userInput = msg.user_input_original || msg.user_input_english;
                                                        assistantOutput = msg.output_original || msg.message || msg.output_english;
                                                    } else {
                                                        userInput = msg.user_input_english || msg.user_input_original;
                                                        assistantOutput = msg.output_english || msg.output_original || msg.message;
                                                    }

                                                    // Fallback for legacy/direct messages that only have msg.message
                                                    if (!userInput && !assistantOutput && msg.message) {
                                                        if (msg.role === 'user') {
                                                            userInput = msg.message;
                                                        } else {
                                                            assistantOutput = msg.message;
                                                        }
                                                    }

                                                    return (
                                                        <React.Fragment key={msg.id || idx}>
                                                            {showDateSeparator && (
                                                                <div className="flex items-center justify-center my-3 sm:my-4">
                                                                    <div className={`px-3 py-1 rounded-full text-[10px] sm:text-[11px] font-medium ${isLight
                                                                        ? "bg-neutral-200 text-neutral-600"
                                                                        : "bg-white/10 text-neutral-400"
                                                                        }`}>
                                                                        {formatFullDate(msg.created_on)}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* User Input Bubble (Guest) - Left Aligned */}
                                                            {userInput && (
                                                                <div className="flex justify-start">
                                                                    <div className="max-w-[85%] sm:max-w-[80%] flex flex-col gap-0.5 sm:gap-1">
                                                                        <div
                                                                            className={`px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-[11px] sm:text-[12px] leading-snug break-words ${isLight
                                                                                ? "bg-white text-neutral-900 border border-neutral-200"
                                                                                : "bg-white/5 text-neutral-50 border border-white/10"
                                                                                }`}
                                                                        >
                                                                            {linkify(userInput, false, isLight)}
                                                                        </div>
                                                                        <span className={`text-[9px] sm:text-[10px] ml-1 ${isLight ? "text-neutral-500" : "text-neutral-500"}`}>
                                                                            {formatTime(msg.created_on)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Assistant Output Bubble (Bot) - Right Aligned */}
                                                            {assistantOutput && (
                                                                <div className="flex justify-end">
                                                                    <div className="max-w-[85%] sm:max-w-[80%] flex flex-col gap-0.5 sm:gap-1">
                                                                        <div
                                                                            className={`px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-[11px] sm:text-[12px] leading-snug break-words ${isLight
                                                                                ? "bg-neutral-900 text-white"
                                                                                : "bg-white text-neutral-900"
                                                                                }`}
                                                                        >
                                                                            {linkify(assistantOutput, true, isLight)}
                                                                        </div>
                                                                        <span className={`text-[9px] sm:text-[10px] text-right mr-1 ${isLight ? "text-neutral-500" : "text-neutral-500"}`}>
                                                                            {formatTime(msg.created_on)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className={`flex-1 flex items-center justify-center ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                                    <div className="text-center">
                                        <div className={`mx-auto w-12 h-12 sm:w-16 sm:h-16 mb-3 rounded-2xl flex items-center justify-center ${isLight ? "bg-neutral-100" : "bg-white/5"}`}>
                                            <svg className={`w-6 h-6 sm:w-8 sm:h-8 ${isLight ? "text-neutral-400" : "text-neutral-500"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                            </svg>
                                        </div>
                                        <div className="text-xs sm:text-sm">Select a conversation to view messages</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Messages;


